import { type JSX, useEffect, useId, useState } from 'react';

import {
  guthaben,
  guthabenErstatten,
  guthabenVerrechnen,
  type Guthabenstand,
  type Guthabenvorgang,
} from '../api/rechnungen';
import { alsEuro } from '../betrag';
import { alsTag } from '../datum';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { alsBetragstext } from '../komponenten/Stornoangaben';
import { Skelett } from '../start/Widget';

type Tat = 'erstatten' | 'verrechnen';

/** Cent als ganze Zahl: Betragstexte vergleichen, ohne mit Gleitkomma zu runden. */
function alsCent(text: string): number {
  const [euro = '0', cent = ''] = text.split('.');
  return Number(euro) * 100 + Number(cent.padEnd(2, '0').slice(0, 2));
}

function alsText(cent: number): string {
  return `${Math.floor(cent / 100)}.${String(cent % 100).padStart(2, '0')}`;
}

/**
 * DAT-12, BUC-145 (AeA-64): Ein Guthaben erstatten oder mit einer anderen
 * offenen Forderung desselben Kunden verrechnen. Beides ist unumkehrbar und
 * fragt im Pop-up nach (Tabelle 29); den Stand — freier Rest, mögliche
 * Zielforderungen — holt die Rückfrage beim Öffnen frisch vom Server, damit
 * zwei Bearbeiter nicht gegen einen alten Rest buchen. Ob es geht, entscheidet
 * der Server; seine Absage steht im Pop-up.
 */
export function Guthabenhandlung({
  posten,
  rechnungsnummer,
  beiErledigt,
}: {
  readonly posten: string;
  readonly rechnungsnummer: string | null;
  readonly beiErledigt: (meldung: string) => void;
}): JSX.Element {
  const [tat, setzeTat] = useState<Tat | null>(null);

  return (
    <>
      <button type="button" className="knopf" onClick={() => setzeTat('erstatten')}>
        Erstatten …
      </button>
      <button type="button" className="knopf" onClick={() => setzeTat('verrechnen')}>
        Verrechnen …
      </button>
      {tat !== null && (
        <Guthabenrueckfrage
          tat={tat}
          posten={posten}
          rechnungsnummer={rechnungsnummer}
          beiSchliessen={() => setzeTat(null)}
          beiErledigt={(meldung) => {
            setzeTat(null);
            beiErledigt(meldung);
          }}
        />
      )}
    </>
  );
}

function Guthabenrueckfrage({
  tat,
  posten,
  rechnungsnummer,
  beiSchliessen,
  beiErledigt,
}: {
  readonly tat: Tat;
  readonly posten: string;
  readonly rechnungsnummer: string | null;
  readonly beiSchliessen: () => void;
  readonly beiErledigt: (meldung: string) => void;
}): JSX.Element {
  const [stand, setzeStand] = useState<Guthabenstand | null>(null);
  const [betrag, setzeBetrag] = useState('');
  const [ziel, setzeZiel] = useState('');
  // Ein Schlüssel je geöffnetem Pop-up: Ein zweiter Klick nach einem Netzfehler bucht nicht doppelt.
  const [schluessel] = useState(() => crypto.randomUUID());
  const betragId = useId();
  const zielId = useId();

  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; ein anderer Posten laedt neu. */
  const geladen = useLadestufe((signal) => guthaben(posten, signal), posten);
  const ladefehler =
    geladen.fehler === undefined
      ? null
      : geladen.fehler instanceof Error
        ? geladen.fehler.message
        : String(geladen.fehler);
  useEffect(() => {
    if (geladen.daten === undefined) return;
    setzeStand(geladen.daten);
    setzeBetrag(geladen.daten.rest.replace('.', ','));
    setzeZiel(geladen.daten.ziele[0]?.id ?? '');
  }, [geladen.daten]);

  const zielposten = stand?.ziele.find((z) => z.id === ziel);
  // DAT-12: nie mehr als das Guthaben, beim Verrechnen auch nie mehr als die Zielforderung offen hat.
  const hoechstCent =
    stand === null
      ? 0
      : tat === 'verrechnen'
        ? Math.min(alsCent(stand.rest), zielposten === undefined ? 0 : alsCent(zielposten.offenerBetrag))
        : alsCent(stand.rest);
  const betragstext = alsBetragstext(betrag);
  const betragCent = betragstext === null ? 0 : alsCent(betragstext);
  const betragFehler =
    stand === null
      ? null
      : betragstext === null
        ? 'Bitte einen Betrag in Euro angeben, etwa 25,00.'
        : betragCent === 0
          ? 'Der Betrag muss größer als 0,00 € sein.'
          : betragCent > hoechstCent
            ? `Höchstens ${alsEuro(alsText(hoechstCent))}.`
            : null;
  const ohneZiel = tat === 'verrechnen' && stand !== null && stand.ziele.length === 0;

  const bestaetigen = async (grund: string): Promise<void> => {
    if (betragstext === null) return;
    if (tat === 'erstatten') {
      await guthabenErstatten(posten, { betrag: betragstext, grund, idempotenzschluessel: schluessel });
      beiErledigt(`${alsEuro(betragstext)} erstattet. Die Übertragung an die Finanzbuchhaltung ist eingereiht.`);
    } else {
      await guthabenVerrechnen(posten, { ziel, betrag: betragstext, grund, idempotenzschluessel: schluessel });
      beiErledigt(
        `${alsEuro(betragstext)} mit ${zielposten?.rechnungsnummer ?? 'der Forderung'} verrechnet. Die Übertragung an die Finanzbuchhaltung ist eingereiht.`,
      );
    }
  };

  return (
    <Rueckfrage
      frage={tat === 'erstatten' ? 'Guthaben erstatten?' : 'Guthaben verrechnen?'}
      unterzeile={rechnungsnummer ?? undefined}
      tat={tat === 'erstatten' ? 'Erstatten' : 'Verrechnen'}
      grund={{ beschriftung: 'Grund', hilfe: 'Steht im Verlauf der Rechnung und geht an die Finanzbuchhaltung.' }}
      gesperrt={stand === null || betragFehler !== null || ohneZiel}
      beiBestaetigen={bestaetigen}
      beiSchliessen={beiSchliessen}
    >
      {stand === null ? (
        <>
          {ladefehler !== null && (
            <p className="meldung meldung--fehler" role="alert">
              {ladefehler}
            </p>
          )}
          {/* Bis die Antwort uebernommen ist, steht weiter das Skelett. */}
          <Ladeanzeige zustand={geladen} was="Das Guthaben" skelett={<Skelett art="liste" />}>
            <Skelett art="liste" />
          </Ladeanzeige>
        </>
      ) : (
        <>
          <p>
            {tat === 'erstatten'
              ? `Frei sind ${alsEuro(stand.rest)}. Die Erstattung lässt sich nicht zurücknehmen; ausgezahlt wird außerhalb des CRM.`
              : `Frei sind ${alsEuro(stand.rest)}. Die Verrechnung mindert das Guthaben und die gewählte Forderung und lässt sich nicht zurücknehmen.`}
          </p>
          {tat === 'verrechnen' &&
            (ohneZiel ? (
              <p className="meldung meldung--hinweis">
                Dieser Kunde hat keine andere offene Forderung, gegen die sich verrechnen ließe.
              </p>
            ) : (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor={zielId}>
                  Zielforderung <span className="feld__pflicht">Pflicht</span>
                </label>
                <select
                  id={zielId}
                  className="feld__eingabe"
                  value={ziel}
                  onChange={(e) => setzeZiel(e.target.value)}
                >
                  {stand.ziele.map((z) => (
                    <option key={z.id} value={z.id}>
                      {`${z.rechnungsnummer ?? 'ohne Nummer'} vom ${alsTag(z.belegdatum) ?? z.belegdatum}, offen ${alsEuro(z.offenerBetrag)}`}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={betragId}>
              Betrag <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id={betragId}
              className="feld__eingabe"
              inputMode="decimal"
              value={betrag}
              aria-invalid={betragFehler !== null}
              onChange={(e) => setzeBetrag(e.target.value)}
            />
            <p className="feld__hilfe">
              {betragFehler ?? `Ein Teilbetrag ist möglich, höchstens ${alsEuro(alsText(hoechstCent))}.`}
            </p>
          </div>
        </>
      )}
    </Rueckfrage>
  );
}

const NAVZUSTAND: Record<NonNullable<Guthabenvorgang['navZustand']>, string> = {
  nicht_uebertragen: 'wartet auf Übertragung',
  in_uebertragung: 'wird übertragen',
  uebertragen: 'an die Finanzbuchhaltung übertragen',
  fehlerhaft: 'Übertragung gescheitert',
};

/**
 * DAT-12, FIB-25: Was mit dem Guthaben schon geschah, und ob es in der
 * Finanzbuchhaltung angekommen ist. Wartet eine Übertragung auf ihre
 * Zuordnung, steht der Grund dabei — sie bucht nicht falsch, sie wartet.
 */
export function Guthabenverlauf({
  posten,
  stand: neuLaden,
}: {
  readonly posten: string;
  /** Ändert sich der Wert, lädt der Verlauf neu — etwa nach einer Erstattung. */
  readonly stand: number;
}): JSX.Element | null {
  const [verlauf, setzeVerlauf] = useState<readonly Guthabenvorgang[]>([]);

  useEffect(() => {
    let aktiv = true;
    guthaben(posten)
      .then((geladen) => {
        if (aktiv) setzeVerlauf(geladen.verlauf);
      })
      .catch(() => {
        if (aktiv) setzeVerlauf([]);
      });
    return () => {
      aktiv = false;
    };
  }, [posten, neuLaden]);

  if (verlauf.length === 0) return null;
  return (
    <ul className="liste">
      {verlauf.map((v) => (
        <li key={`${v.art}-${v.id}`}>
          {`${alsTag(v.am) ?? v.am}: ${alsEuro(v.betrag)} ${
            v.art === 'erstattung' ? 'erstattet' : `verrechnet mit ${v.zielrechnungsnummer ?? 'einer Forderung'}`
          }${v.grund === null ? '' : ` (${v.grund})`} — ${v.navZustand === null ? 'nicht eingereiht' : NAVZUSTAND[v.navZustand]}`}
          {v.navMeldung !== null && v.navZustand !== 'uebertragen' && <p className="feld__hilfe">{v.navMeldung}</p>}
        </li>
      ))}
    </ul>
  );
}
